/**
 * <nosto-dynamic-card handle="..." template="card">
 *
 * Web component that hydrates itself with the HTML of a Shopify product
 * card by fetching `/products/{handle}?view={template}` (template defaults
 * to "card" → templates/product.card.liquid; the search drawer autocomplete
 * uses "card-search" → templates/product.card-search.liquid). This
 * guarantees that Nosto-rendered grids are pixel-iso with the native theme
 * card.
 *
 * Why a custom element here instead of the built-in one shipped by the
 * Nosto SDK: we need to register `<nosto-dynamic-card>` BEFORE the SDK
 * loads (via content_for_header), so the browser picks ours up first.
 * The script tag for this file must therefore run synchronously above
 * `{{ content_for_header }}` in theme.liquid.
 *
 * Behaviour:
 *  - Concurrent fetches are capped (MAX_CONCURRENT) to avoid hammering
 *    the storefront when a large grid renders.
 *  - Successful fetches are cached in memory for the page lifetime so
 *    that pagination / sort changes that re-render the same handle do
 *    not refetch.
 *  - Cards outside the viewport are deprioritised until visible.
 */

(function () {
  if (window.customElements && customElements.get("nosto-dynamic-card")) return;

  const MAX_CONCURRENT = 6;
  const cache = new Map();
  const pending = new Map();
  const queue = [];
  let inFlight = 0;

  function shopifyRoot() {
    const root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    return root && root !== "/" ? root.replace(/\/$/, "") : "";
  }

  function buildUrl(handle, template) {
    return `${shopifyRoot()}/products/${encodeURIComponent(handle)}?view=${encodeURIComponent(template)}`;
  }

  function pump() {
    while (inFlight < MAX_CONCURRENT && queue.length) {
      const job = queue.shift();
      if (job.cancelled) continue;
      inFlight++;
      fetch(job.url, { credentials: "same-origin" })
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => {
          cache.set(job.key, html);
          job.resolve(html);
        })
        .catch((err) => job.reject(err))
        .finally(() => {
          inFlight--;
          pending.delete(job.key);
          pump();
        });
    }
  }

  function fetchCard(handle, template, priority) {
    const key = `${template}:${handle}`;
    if (cache.has(key)) return Promise.resolve(cache.get(key));
    if (pending.has(key)) return pending.get(key);

    const promise = new Promise((resolve, reject) => {
      const job = { key, url: buildUrl(handle, template), resolve, reject, cancelled: false };
      if (priority) queue.unshift(job);
      else queue.push(job);
      pump();
    });
    pending.set(key, promise);
    return promise;
  }

  class NostoDynamicCard extends HTMLElement {
    static get observedAttributes() {
      return ["handle", "template"];
    }

    get handle() {
      return this.getAttribute("handle");
    }
    set handle(value) {
      if (value == null) this.removeAttribute("handle");
      else this.setAttribute("handle", String(value));
    }

    get template() {
      return this.getAttribute("template") || "card";
    }
    set template(value) {
      if (value == null) this.removeAttribute("template");
      else this.setAttribute("template", String(value));
    }

    connectedCallback() {
      this._observe();
    }

    disconnectedCallback() {
      if (this._observer) {
        this._observer.disconnect();
        this._observer = null;
      }
    }

    attributeChangedCallback(name, oldValue, newValue) {
      // Preact sets attributes one by one BEFORE inserting the element:
      // rendering here would fire a fetch with a half-initialised state
      // (handle set, template still missing → wrong view). Initial render
      // happens in connectedCallback; only react to later mutations.
      if (oldValue !== newValue && this.isConnected) {
        this._rendered = false;
        this._render(false);
      }
    }

    _observe() {
      if (!this.handle) return;
      if (typeof IntersectionObserver === "undefined") {
        this._render(false);
        return;
      }
      this._observer = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (entry.isIntersecting) {
              this._observer.disconnect();
              this._observer = null;
              this._render(true);
              break;
            }
          }
        },
        { rootMargin: "400px 0px" }
      );
      this._observer.observe(this);
      // Fire a low-priority fetch immediately so the cache warms up.
      this._render(false);
    }

    _render(priority) {
      const handle = this.handle;
      if (!handle || this._rendered) return;
      this._rendered = true;
      fetchCard(handle, this.template, priority)
        .then((html) => {
          if (!this.isConnected) return;
          this.innerHTML = html;
          this.dispatchEvent(new CustomEvent("nosto:card-loaded", { bubbles: true }));
        })
        .catch(() => {
          this._rendered = false;
        });
    }
  }

  customElements.define("nosto-dynamic-card", NostoDynamicCard);
})();
